<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import {
  CUSTOM_SYSTEM_CODE,
  SELECTABLE_SYSTEM_CODES,
  getSystemByCode,
  type NotationSystemCode,
} from "@/constants/notationSystems";

const props = defineProps<{
  modelValue: NotationSystemCode;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: NotationSystemCode): void;
}>();

const { t } = useTranslation();

const labelOf = (code: NotationSystemCode): string =>
  code === CUSTOM_SYSTEM_CODE
    ? t("report:diagnostics.finding.item.notationCustom")
    : (getSystemByCode(code)?.label ?? code);

const options = computed(() =>
  SELECTABLE_SYSTEM_CODES.map((code) => ({ code, label: labelOf(code) })),
);

const currentLabel = computed(() => labelOf(props.modelValue));
</script>

<template>
  <el-dropdown
    trigger="click"
    @command="(code: NotationSystemCode) => emit('update:modelValue', code)"
  >
    <button
      type="button"
      class="notation-system-badge"
      :title="t('report:diagnostics.finding.item.notationSystem')"
      :aria-label="t('report:diagnostics.finding.item.notationSystem')"
    >
      <span>{{ currentLabel }}</span>
      <i class="bi-chevron-down"></i>
    </button>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item
          v-for="option in options"
          :key="option.code"
          :command="option.code"
        >
          <span :class="{ 'fw-semibold': option.code === modelValue }">
            {{ option.label }}
          </span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<style lang="scss" scoped>
// Réglage secondaire : présent quand on le cherche, silencieux le reste du temps.
.notation-system-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 6px;
  border: none;
  border-radius: 4px;
  background-color: transparent;
  color: var(--el-text-color-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.6;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover,
  &:focus-visible {
    background-color: var(--el-fill-color-light);
    color: var(--el-text-color-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--el-color-primary);
    outline-offset: 1px;
  }

  i {
    font-size: 0.65rem;
    opacity: 0.7;
  }
}
</style>
